import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Use ref to ensure we have the latest user data
  const userRef = useRef(null);
  const loadingRef = useRef(true);

  // Synchronous user update function
  const updateUserSync = useCallback((userData) => {
    
    // Update refs immediately
    userRef.current = userData;
    loadingRef.current = false;
    
    // Update state
    setUser(userData);
    setIsAuthenticated(!!userData);
    setLoading(false);
    setIsInitialized(true);
    
    // Store in localStorage
    if (userData) {
      localStorage.setItem('userDetails', JSON.stringify(userData));
    }
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      setLoading(true);
      loadingRef.current = true;
      
      try {
        const storedUser = localStorage.getItem('userDetails');
        const accessToken = localStorage.getItem('accessToken');

        if (accessToken && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);

            if (parsedUser && parsedUser.id) {
              // Use synchronous update
              updateUserSync(parsedUser);
              setError(null);
            } else {
              clearAuthData();
              setError('Invalid user data');
            }
          } catch (parseError) {
            clearAuthData();
            setError('Failed to parse user data');
          }
        } else {
          clearAuthData();
        }
      } catch (err) {
        clearAuthData();
        setError('Authentication check failed');
      } finally {
        if (!userRef.current) {
          setLoading(false);
          loadingRef.current = false;
          setIsInitialized(true);
        }
      }
    };

    checkAuth();
  }, [updateUserSync]);

  const clearAuthData = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userDetails');
    localStorage.removeItem('refreshToken');
    
    userRef.current = null;
    loadingRef.current = false;
    
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
    setIsInitialized(true);
  }, []);

  const logout = useCallback((navigate) => {
    clearAuthData();
    if (navigate) {
      navigate('/login');
    } else {
      // window.location.href = '/login';
    }
  }, [clearAuthData]);

  // Use refs for immediate access to current values
  const hasPermission = useCallback(
    (moduleValue, operations = ['list'], appValue = 'placement') => {
      // Get current values from refs for immediate access
      const currentUser = userRef.current;
      const currentLoading = loadingRef.current;
      
      // Don't check permissions while loading or not initialized
      if (currentLoading || !isInitialized) {
        return false;
      }
      
      // If no user, deny access
      if (!currentUser) {
        return false;
      }
      
      // SuperAdmin gets full access
      if (currentUser.isSuperAdmin === true || currentUser.isSuperAdmin === 'true') {
        return true;
      }

      // Normalize operations to always be an array
      const requiredOps = Array.isArray(operations) ? operations : [operations];

      // Check profiles if they exist
      if (currentUser.profiles && Array.isArray(currentUser.profiles) && currentUser.profiles.length > 0) {
        for (const profile of currentUser.profiles) {
          for (const app of profile.apps || []) {
            if (app.value !== appValue) continue;

            for (const mod of app.modules || []) {
              if (mod.value !== moduleValue) continue;

              const userOps = mod.operations?.map((op) => op.value) || [];
              
              // Check if every required operation is present
              if (requiredOps.every((op) => userOps.includes(op))) {
                return true;
              }
            }
          }
        }
      }

      return false;
    },
    [isInitialized]
  );

  const contextValue = {
    user,
    setUser,
    updateUser: updateUserSync,
    loading,
    setLoading,
    error,
    setError,
    isAuthenticated,
    setIsAuthenticated,
    isInitialized,
    hasPermission,
    logout,
  };

  // Don't render children until initialized
  if (!isInitialized) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Initializing...</span>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {error && !loading ? (
        <div className="auth-error alert alert-danger text-center">
          <h5>Authentication Error</h5>
          <p>{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => {
              clearAuthData();
              setError(null);
            }}
          >
            Go to Login
          </button>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Enhanced withAuth HOC
export const withAuth = (WrappedComponent) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, loading, isInitialized } = useAuth();

    // Wait for initialization
    if (!isInitialized || loading) {
      return (
        <div className="d-flex justify-content-center align-items-center min-vh-100">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    return <WrappedComponent {...props} />;
  };
};

export default AuthContext;
